import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuthFetch } from '../lib/useAuthFetch'
import { useAccess } from '../hooks/useAccess'
import { AccessDenied } from '../components/shared/AccessDenied'
import { Card, PageHeader, Pill, SectionLabel } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''
type RivalAllowance = 2 | 5 | 10

interface Plan { code: string; rivals: RivalAllowance; monthlyCents: number }
interface LocationPlan {
  location_id: string
  location_name: string
  rival_allowance: RivalAllowance | null
  tracked_rivals: number
}
interface Pricing {
  locationCount: number
  discountPercent: number
  quoteRequired: boolean
  baseMonthlyCents: number
  discountCents: number | null
  monthlyTotalCents: number | null
}
interface PlanPreview {
  billing: {
    billing_status: string
    billing_exempt: boolean
    trial_usable_data_ready_at: string | null
    trial_started_at: string | null
    trial_ends_at: string | null
    billing_period_end: string | null
    billing_cancel_at_period_end: boolean
  }
  plans: Plan[]
  locations: LocationPlan[]
  all_configured: boolean
  over_allowance: LocationPlan[]
  ready_for_checkout: boolean
  quote_required: boolean
  pricing: Pricing | null
}
const money = (cents: number | null | undefined) => cents == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
}).format(cents / 100)

const planDescription: Record<RivalAllowance, string> = {
  2: 'For a focused local watchlist',
  5: 'For the rivals that shape daily decisions',
  10: 'For full competitive coverage',
}

function trialMessage(preview: PlanPreview): string {
  const { billing } = preview
  if (billing.billing_exempt) return 'Internal access — this organization is not billed.'
  if (billing.billing_status === 'trial_pending') return 'Your 14-day free trial starts when your own menu and tracked-rival data are ready.'
  if (billing.billing_status === 'trial_active' && billing.trial_ends_at) {
    return `Free trial active through ${new Date(billing.trial_ends_at).toLocaleDateString()}. No card is required and it will not convert automatically.`
  }
  if (billing.billing_status === 'trial_ended') return 'Your free trial has ended. Choose a plan for every location to start a monthly subscription.'
  if (billing.billing_status === 'checkout_pending') return 'Checkout is in progress.'
  if (billing.billing_status === 'active') return 'Monthly subscription active.'
  if (billing.billing_status === 'past_due') return 'Payment needs attention. Use Manage payment to restore billing.'
  if (billing.billing_status === 'canceling') return 'Subscription cancellation is scheduled for the end of the paid period.'
  if (billing.billing_status === 'canceled') return 'Subscription ended. Your locations and rival choices are preserved.'
  return 'Your 14-day free trial starts when your own menu and tracked-rival data are ready. No card is required.'
}

export default function BillingUsage() {
  const authFetch = useAuthFetch()
  const { access, loading: accessLoading } = useAccess()
  const [preview, setPreview] = useState<PlanPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingLocation, setSavingLocation] = useState<string | null>(null)
  const [startingCheckout, setStartingCheckout] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setMessage(null)
    try {
      const response = await authFetch(`${API}/api/v1/billing/plan-preview`)
      const body = await response.json()
      if (!response.ok || !body?.success) throw new Error(body?.error || 'Could not load pricing.')
      setPreview(body.data)
    } catch (error: any) {
      setMessage(error?.message || 'Could not load pricing.')
    } finally {
      setLoading(false)
    }
  }, [authFetch])

  useEffect(() => { void load() }, [load])

  const selectPlan = async (locationId: string, allowance: RivalAllowance) => {
    setSavingLocation(locationId)
    setMessage(null)
    try {
      const response = await authFetch(`${API}/api/v1/billing/locations/${locationId}/plan`, {
        method: 'PATCH', body: JSON.stringify({ rival_allowance: allowance }),
      })
      const body = await response.json()
      if (!response.ok || !body?.success) throw new Error(body?.error || 'Could not save this plan.')
      await load()
    } catch (error: any) {
      setMessage(error?.message || 'Could not save this plan.')
    } finally {
      setSavingLocation(null)
    }
  }

  const beginCheckout = async () => {
    setStartingCheckout(true)
    setMessage(null)
    try {
      const response = await authFetch(`${API}/api/v1/billing/checkout`, {
        method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      const body = await response.json()
      if (!response.ok || !body?.success || !body?.data?.url) throw new Error(body?.error || 'Could not open checkout.')
      window.location.assign(body.data.url)
    } catch (error: any) {
      setMessage(error?.message || 'Could not open checkout.')
      setStartingCheckout(false)
    }
  }

  const openPortal = async () => {
    setMessage(null)
    try {
      const response = await authFetch(`${API}/api/v1/billing/portal`, { method: 'POST' })
      const body = await response.json()
      if (!response.ok || !body?.success || !body?.data?.url) throw new Error(body?.error || 'Could not open billing.')
      window.location.assign(body.data.url)
    } catch (error: any) {
      setMessage(error?.message || 'Could not open billing.')
    }
  }

  const paidStatus = !!preview && ['active', 'past_due', 'canceling'].includes(preview.billing.billing_status)
  const summary = useMemo(() => preview?.pricing ? {
    base: money(preview.pricing.baseMonthlyCents),
    savings: money(preview.pricing.discountCents),
    total: money(preview.pricing.monthlyTotalCents),
  } : null, [preview])

  if (!accessLoading && access && access.capabilities?.view_billing !== true) {
    return <AccessDenied title="Billing access required" detail="Ask an account owner or administrator for billing access." />
  }

  const button: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text-1)',
    padding: '9px 13px', cursor: 'pointer', fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700,
  }

  const fallbackPlans: Plan[] = [
    { code: 'rivals_2', rivals: 2, monthlyCents: 14_900 },
    { code: 'rivals_5', rivals: 5, monthlyCents: 19_900 },
    { code: 'rivals_10', rivals: 10, monthlyCents: 29_900 },
  ]

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg)', color: 'var(--text-1)', overflowY: 'auto' }}>
      <PageHeader title="Pricing & billing" subtitle="One monthly plan per location · every core feature included" variant="compact"
        actions={paidStatus ? <button style={button} onClick={openPortal}>Manage payment</button> : undefined} />

      <main style={{ padding: 24, maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 18 }}>
        {message && <div role="alert" style={{ border: '1px solid var(--rose)', borderRadius: 8, padding: '10px 12px', color: 'var(--rose)', fontSize: 12 }}>{message}</div>}

        <Card style={{ padding: 18 }}>
          <Pill tone={preview?.billing.billing_status === 'trial_active' ? 'accent' : 'neutral'}>
            {preview?.billing.billing_status?.replace(/_/g, ' ') || 'Loading'}
          </Pill>
          <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6 }}>{preview ? trialMessage(preview) : 'Loading your billing status…'}</div>
        </Card>

        <section>
          <SectionLabel>Monthly plans</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 8 }}>
            {(preview?.plans ?? fallbackPlans).map((plan) => (
              <Card key={plan.code} style={{ padding: 18 }} data-testid={`billing-plan-${plan.rivals}`}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Up to {plan.rivals} rivals</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 28, fontWeight: 700, margin: '8px 0 4px' }}>
                  {money(plan.monthlyCents)}<span style={{ fontSize: 11, color: 'var(--text-3)' }}>/location/mo</span>
                </div>
                <div style={{ color: 'var(--text-2)', fontSize: 12 }}>{planDescription[plan.rivals]}</div>
                <div style={{ marginTop: 12, color: 'var(--text-2)', fontSize: 11, lineHeight: 1.55 }}>All alerts, price comparison, daily decisions, and the weekly competitive brief included.</div>
              </Card>
            ))}
          </div>
        </section>

        <Card style={{ padding: 18 }}>
          <SectionLabel>Plans by location</SectionLabel>
          {loading ? <div style={{ padding: '18px 0', color: 'var(--text-3)' }}>Loading locations…</div>
            : !preview?.locations.length ? <div style={{ padding: '18px 0', color: 'var(--text-3)' }}>Add a location before choosing a plan.</div>
            : <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                {preview.locations.map((location) => (
                  <div key={location.location_id} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
                    <div><div style={{ fontWeight: 700 }}>{location.location_name}</div><div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginTop: 3 }}>{location.tracked_rivals} rival{location.tracked_rivals === 1 ? '' : 's'} currently tracked</div></div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {([2, 5, 10] as RivalAllowance[]).map((allowance) => {
                        const selected = location.rival_allowance === allowance
                        const disabled = savingLocation === location.location_id || location.tracked_rivals > allowance || paidStatus
                        return <button key={allowance} disabled={disabled} onClick={() => selectPlan(location.location_id, allowance)} aria-pressed={selected}
                          style={{ ...button, borderColor: selected ? 'var(--accent)' : 'var(--border)', color: selected ? 'var(--accent)' : 'var(--text-2)', opacity: disabled && !selected ? .45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>{allowance} rivals</button>
                      })}
                    </div>
                  </div>
                ))}
              </div>}
        </Card>

        <Card style={{ padding: 18 }}>
          <SectionLabel>Account total</SectionLabel>
          {preview?.quote_required ? <div style={{ marginTop: 10, fontSize: 14 }}>25+ locations use a custom quote. Contact CannaSpy before checkout.</div>
            : summary ? <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Base monthly total</span><strong>{summary.base}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--accent)' }}><span>{preview?.pricing?.discountPercent}% multi-location discount</span><strong>−{summary.savings}</strong></div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 18 }}><strong>Monthly total</strong><strong data-testid="billing-monthly-total">{summary.total}</strong></div>
              </div>
            : <div style={{ marginTop: 10, color: 'var(--text-3)' }}>Choose a plan for every active location to see the exact total.</div>}
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-3)', lineHeight: 1.55 }}>Volume discount: 5–9 locations 10% · 10–14 20% · 15–19 25% · 20–24 30% · 25+ custom quote.</div>
          {!paidStatus && !preview?.billing.billing_exempt && <button data-testid="billing-start-checkout" disabled={!preview?.ready_for_checkout || startingCheckout} onClick={beginCheckout}
            style={{ ...button, marginTop: 16, background: preview?.ready_for_checkout ? 'var(--accent)' : 'var(--surface-2)', borderColor: preview?.ready_for_checkout ? 'var(--accent)' : 'var(--border)', color: preview?.ready_for_checkout ? 'var(--bg)' : 'var(--text-3)', cursor: preview?.ready_for_checkout ? 'pointer' : 'not-allowed' }}>{startingCheckout ? 'Opening checkout…' : 'Start monthly subscription'}</button>}
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-3)' }}>Monthly only. Your free trial never converts or charges automatically.</div>
        </Card>

        {paidStatus && preview?.billing.billing_status !== 'canceling' && (
          <Card style={{ padding: 18, borderColor: 'var(--rose)' }}>
            <SectionLabel>Cancel subscription</SectionLabel>
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6 }}>Cancel renewal at the end of the paid month. Your locations and rival selections stay saved.</div>
            <button className="btn" style={{ marginTop: 12, color: 'var(--rose)' }} onClick={() => window.location.assign('/cancel')}>Review cancellation</button>
          </Card>
        )}
      </main>
    </div>
  )
}
